import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragMoveEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  FolderPlus,
  GripVertical,
  Plus,
 Layers } from "lucide-react";
import type { ManualBodySection } from "@arc/contracts";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { useManualBodyMutations } from "../useManualMutations.js";
import type { BodyWriteGate } from "./useBodyWriteGate.js";
import { InsertSlot, RenameInput, SectionActionsMenu, StepActionsMenu, createSectionAt, createStepAt } from "./outline-controls.js";
import { resolveStepDrop, type StepDropTarget } from "./outline-order.js";
import { STEP_TYPE_ICON } from "./step-type-icon.js";

type Mutations = ReturnType<typeof useManualBodyMutations>;
type Editing = { kind: "section" | "step"; id: number } | null;
/** `beforeId` = inserted in front of that row; absent = appended at the end. */
type Adding = { kind: "section"; beforeId?: number } | { kind: "step"; sectionId: number; beforeId?: number } | null;

/**
 * The authoring OUTLINE (ADR-002 Phase 10) — the sections→steps tree with structural editing, forked from the reader's
 * `InhaltTree`. **Section = folder, Step = file** (strict 2-level, legacy parity). Steps move by pointer drag, and the
 * **kebab** (Move up / down / to section…) stays the accessible path — there is no `KeyboardSensor` anywhere in the app. Add
 * uses an inline input (no default-name collisions), at the end or at an insert slot between rows. Every op sends `expectedVersion` and — for reorder/move — the WHOLE ordered sibling id-list (permutation).
 * Delete-last-section is disabled client-side (the server also 409s `BODY_LAST_SECTION`). Selecting a step scrolls the
 * main pane to it. Preflight is off → the raw `<button>`/`<ul>` UA styles are reset (`border-0`, `bg-transparent`, `list-none`).
 */
export function OutlineEditorPanel({
  sections,
  currentStepId,
  gate,
  disabled,
  m,
  onSelectStep,
  onDragActiveChange,
  query = "",
  onCollapse,
  allSteps = null,
}: {
  sections: ManualBodySection[];
  currentStepId: number | null;
  /** The legacy "Alle Schritte" entry (ADR-028) — AR manuals only (`null` hides it): the manual-wide element set,
   *  rendered above the sections as a step-shaped entry with its element count. */
  allSteps?: { count: number; selected: boolean; onSelect: () => void } | null;
  /** The editor-wide serialized write gate — every structural op enqueues through it so it can't race a step autosave into
   *  a self-inflicted 409 (the gate supplies the CAS version left by the prior write). Errors surface via mutation state. */
  gate: BodyWriteGate;
  disabled: boolean;
  m: Mutations;
  onSelectStep: (stepId: number, sectionId?: number) => void;
  /** The editor's search: filters the tree by section and step name. */
  query?: string;
  /** Fold the outline back to its dots. */
  onCollapse?: () => void;
  /** A step drag started or ended. Escape then cancels the drag, so the editor around it must not close on it. */
  onDragActiveChange?: (active: boolean) => void;
}) {
  const { t } = useTranslation();
  const { enqueue } = gate;
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set(sections.map((s) => s.id)));
  const [editing, setEditing] = useState<Editing>(null);
  const [adding, setAdding] = useState<Adding>(null);
  const navRef = useRef<HTMLElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const pointerY = useRef(0);
  const [dragged, setDragged] = useState<{ id: number; name: string } | null>(null);
  const [dropTarget, setDropTarget] = useState<StepDropTarget | null>(null);
  const [moving, setMoving] = useState(false);
  // A small movement starts a drag, so a plain click still selects the step.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  // The highlighted step follows the editor's scrolling, so keep it visible when the outline is longer than the panel.
  useEffect(() => {
    navRef.current?.querySelector('[aria-current="step"]')?.scrollIntoView?.({ block: "nearest" });
  }, [currentStepId]);

  // Open a section when the current step ENTERS it. Only then: the highlight follows scrolling and the body changes on every
  // save, and either one would otherwise reopen a section the author just collapsed.
  const currentSectionId = sections.find((s) => s.steps.some((st) => st.id === currentStepId))?.id;
  const openedFor = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (currentSectionId === undefined || currentSectionId === openedFor.current) return;
    openedFor.current = currentSectionId;
    setExpanded((prev) => (prev.has(currentSectionId) ? prev : new Set(prev).add(currentSectionId)));
  }, [currentSectionId]);

  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Expand/collapse ALL sections (VSCode-style single toggle): all-open ⇒ collapse to none; otherwise expand every section.
  const allExpanded = sections.length > 0 && sections.every((s) => expanded.has(s.id));
  const toggleAll = () => setExpanded(allExpanded ? new Set() : new Set(sections.map((s) => s.id)));

  const stepCount = sections.reduce((n, s) => n + s.steps.length, 0);

  // Search filter (navigation aid): keep a section if its NAME matches or it holds a matching step; a name-match shows all
  // its steps, else only the matching ones. `rows` carries the REAL section object (so reorder/move still use the full
  // `sec.steps` / `sectionOrder`), only the RENDERED step subset differs. Empty query ⇒ the full tree.
  const q = query.trim().toLowerCase();
  const rows =
    q === ""
      ? sections.map((sec) => ({ sec, steps: sec.steps }))
      : sections
          .map((sec) => {
            const nameHit = sec.name.toLowerCase().includes(q);
            const steps = nameHit ? sec.steps : sec.steps.filter((st) => st.name.toLowerCase().includes(q));
            return { sec, steps, include: nameHit || steps.length > 0 };
          })
          .filter((r) => r.include)
          .map(({ sec, steps }) => ({ sec, steps }));

  const commitAdd = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed || !adding) return setAdding(null);
    const target = adding; // capture before the async enqueue clears it (the vars closure runs a microtask later)
    setAdding(null);
    if (target.kind === "section") createSectionAt(gate, m, { name: trimmed, beforeId: target.beforeId });
    else void createStepAt(gate, m, { sectionId: target.sectionId, name: trimmed, beforeId: target.beforeId }).then((id) => id !== undefined && onSelectStep(id));
  };
  const commitRename = (kind: "section" | "step", id: number, name: string, prev: string) => {
    const trimmed = name.trim();
    setEditing(null);
    if (!trimmed || trimmed === prev) return;
    if (kind === "section") void enqueue(m.renameSection, (expectedVersion) => ({ expectedVersion, sectionId: id, name: trimmed })).catch(() => {});
    else void enqueue(m.renameStep, (expectedVersion) => ({ expectedVersion, stepId: id, name: trimmed })).catch(() => {});
  };

  // The row under the pointer; between rows the nearest one, so the gaps don't lose the target. Outside the list: none, so
  // a drop on the search or the footer moves nothing.
  const collisionDetection: CollisionDetection = (args) => {
    const panel = listRef.current?.getBoundingClientRect();
    const pointer = args.pointerCoordinates;
    if (!panel || !pointer) return [];
    pointerY.current = pointer.y;
    // Checked first: rows scrolled out of the list keep their rects, under the search field or the footer.
    const inside = pointer.x >= panel.left && pointer.x <= panel.right && pointer.y >= panel.top && pointer.y <= panel.bottom;
    if (!inside) return [];
    const hits = pointerWithin(args);
    return hits.length > 0 ? hits : closestCenter(args);
  };
  const onDragStart = (e: DragStartEvent) => {
    const data = e.active.data.current as { stepId: number; name: string } | undefined;
    if (data) setDragged({ id: data.stepId, name: data.name });
    onDragActiveChange?.(true);
  };
  const targetOf = (e: DragMoveEvent | DragEndEvent): StepDropTarget | null => {
    const over = e.over?.data.current as { stepId?: number; sectionId?: number } | undefined;
    if (!over || !e.over) return null;
    if (over.sectionId !== undefined) return { kind: "section", sectionId: over.sectionId };
    if (over.stepId === undefined || over.stepId === (e.active.data.current as { stepId: number } | undefined)?.stepId) return null;
    return { kind: "step", stepId: over.stepId, before: pointerY.current < e.over.rect.top + e.over.rect.height / 2 };
  };
  const onDragMove = (e: DragMoveEvent) => setDropTarget(targetOf(e));
  const endDrag = () => {
    onDragActiveChange?.(false);
    setDragged(null);
    setDropTarget(null);
  };
  const onDragEnd = (e: DragEndEvent) => {
    const stepId = (e.active.data.current as { stepId: number } | undefined)?.stepId;
    const target = targetOf(e);
    const write = stepId !== undefined && target ? resolveStepDrop(sections, stepId, target) : null;
    endDrag();
    if (!write || stepId === undefined) return;
    // Then show the moved step. Its section is passed along, because the caller's own list is still the one before this write.
    // No further drag until this one has landed: its order is built from `sections`, so a second drop would send a whole order
    // that undoes this move.
    setMoving(true);
    const written =
      write.kind === "reorder"
        ? enqueue(m.reorderSteps, (expectedVersion) => ({ expectedVersion, sectionId: write.sectionId, order: write.order })).then(() => onSelectStep(stepId, write.sectionId))
        : enqueue(m.moveStep, (expectedVersion) => ({ expectedVersion, stepId, toSectionId: write.toSectionId, order: write.order })).then(() => onSelectStep(stepId, write.toSectionId));
    void written.catch(() => {}).finally(() => setMoving(false));
  };
  // Structure can only change on the full tree: a search shows a subset, so a position in it says nothing.
  const canEditStructure = !disabled && q === "";

  return (
    <nav ref={navRef} aria-label={t("manualEditor.outline")} className="border-border flex h-full w-full flex-col border-r">
      <div className="border-border flex items-center justify-between gap-2 border-b px-3 py-2.5">
        <span className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">{t("manualEditor.outline")}</span>
        <div className="flex items-center gap-1">
          <span className="text-muted-foreground text-xs whitespace-nowrap tabular-nums">{t("manualEditor.countSteps", { count: stepCount })}</span>
          {/* Expand/collapse ALL sections (VSCode-style). Hidden during search — the filter force-expands matches. */}
          {q === "" && sections.length > 0 ? (
            <TooltipTrigger delay={0}>
              <Button variant="ghost" size="icon-xs" aria-label={allExpanded ? t("manualEditor.collapseAllSections") : t("manualEditor.expandAllSections")} onPress={toggleAll} className="text-muted-foreground shrink-0">
                {allExpanded ? <ChevronsDownUp aria-hidden="true" className="size-3.5" /> : <ChevronsUpDown aria-hidden="true" className="size-3.5" />}
              </Button>
              <Tooltip>{allExpanded ? t("manualEditor.collapseAll") : t("manualEditor.expandAll")}</Tooltip>
            </TooltipTrigger>
          ) : null}
          {onCollapse ? (
            <TooltipTrigger delay={0} focusable={false}>
              <Button variant="outline" size="icon-sm" aria-label={t("manualEditor.collapseOutline")} onPress={onCollapse} className="dark:border-border ml-1 shrink-0">
                <ChevronLeft aria-hidden="true" className="size-4" />
              </Button>
              <Tooltip>{t("manualEditor.collapseOutline")}</Tooltip>
            </TooltipTrigger>
          ) : null}
        </div>
      </div>

      <DndContext sensors={sensors} collisionDetection={collisionDetection} onDragStart={onDragStart} onDragMove={onDragMove} onDragEnd={onDragEnd} onDragCancel={endDrag}>
      <ul ref={listRef} className="flex min-h-0 flex-1 list-none flex-col gap-1 overflow-auto p-3">
        {/* t("manualEditor.allSteps2") — the legacy's first outline entry on an AR manual (`ARLMS_AllSteps.js`). It is not a section:
            no rename, no reorder, no steps of its own; selecting it swaps the main pane to the all-steps card. Kept
            out of the search filter, since it is one fixed entry, not content. */}
        {allSteps != null && q === "" ? (
          <li>
            <button
              type="button"
              aria-current={allSteps.selected ? "true" : undefined}
              onClick={allSteps.onSelect}
              className={cn(
                "hover:bg-muted focus-visible:ring-primary flex w-full cursor-pointer items-center justify-between gap-2 rounded-md border-0 bg-transparent px-2 py-1.5 text-left text-xs font-semibold tracking-wide uppercase focus-visible:ring-1 focus-visible:outline-none",
                allSteps.selected ? "bg-muted text-foreground" : "text-muted-foreground",
              )}
            >
              <span className="flex items-center gap-1.5">
                <Layers aria-hidden="true" className="size-3.5" />
                {t("manualEditor.allSteps")}
              </span>
              <span className="text-muted-foreground font-normal tabular-nums normal-case">{t("manualEditor.countElements", { count: allSteps.count })}</span>
            </button>
          </li>
        ) : null}
        {rows.length === 0 ? <li className="text-muted-foreground px-2 py-6 text-center text-xs">No matching sections or steps.</li> : null}
        {rows.map(({ sec, steps }) => {
          const open = q !== "" || expanded.has(sec.id);
          return (
            <li key={sec.id}>
              {canEditStructure ? (
                adding?.kind === "section" && adding.beforeId === sec.id ? (
                  <RenameInput initial="" placeholder={t("manualEditor.sectionName")} onCommit={commitAdd} onCancel={() => setAdding(null)} />
                ) : (
                  <InsertSlot label={t("manualEditor.insertSectionBefore", { name: sec.name })} hidden={dragged !== null} onPress={() => setAdding({ kind: "section", beforeId: sec.id })} />
                )
              ) : null}
              <SectionDropZone sectionId={sec.id} disabled={!canEditStructure} isTarget={dropTarget?.kind === "section" && dropTarget.sectionId === sec.id}>
                <button
                  type="button"
                  aria-expanded={open}
                  aria-label={t(open ? "manualEditor.collapseSection" : "manualEditor.expandSection", { name: sec.name })}
                  onClick={() => toggle(sec.id)}
                  className="text-muted-foreground hover:text-foreground shrink-0 cursor-pointer rounded-sm border-0 bg-transparent p-0.5"
                >
                  {open ? <ChevronDown aria-hidden="true" className="size-3.5" /> : <ChevronRight aria-hidden="true" className="size-3.5" />}
                </button>
                {editing?.kind === "section" && editing.id === sec.id ? (
                  <RenameInput initial={sec.name} onCommit={(v) => commitRename("section", sec.id, v, sec.name)} onCancel={() => setEditing(null)} />
                ) : (
                  <button
                    type="button"
                    onClick={() => toggle(sec.id)}
                    className="text-muted-foreground min-w-0 flex-1 cursor-pointer truncate border-0 bg-transparent px-1 py-1.5 text-left text-xs font-semibold tracking-wide uppercase"
                  >
                    {sec.name}
                  </button>
                )}
                {!disabled ? (
                  <SectionActionsMenu
                    section={sec}
                    sections={sections}
                    gate={gate}
                    m={m}
                    onRename={() => setEditing({ kind: "section", id: sec.id })}
                    onInsert={canEditStructure ? (beforeId) => setAdding({ kind: "section", beforeId }) : undefined}
                  />
                ) : null}
              </SectionDropZone>

              {open ? (
                <ul className="mt-0.5 flex list-none flex-col gap-0.5 p-0 pl-4">
                  {steps.map((st) => {
                    const active = st.id === currentStepId;
                    const Icon = STEP_TYPE_ICON[st.type];
                    const draggable = canEditStructure && editing === null && !moving;
                    return (
                      <StepRow
                        key={st.id}
                        step={st}
                        draggable={draggable}
                        dropEdge={dropTarget?.kind === "step" && dropTarget.stepId === st.id ? (dropTarget.before ? "top" : "bottom") : null}
                        slot={
                          canEditStructure ? (
                            adding?.kind === "step" && adding.beforeId === st.id ? (
                              <div className="pl-2">
                                <RenameInput initial="" placeholder={t("manualEditor.stepName")} onCommit={commitAdd} onCancel={() => setAdding(null)} />
                              </div>
                            ) : (
                              <InsertSlot label={t("manualEditor.insertStepBefore", { name: st.name })} hidden={dragged !== null} onPress={() => setAdding({ kind: "step", sectionId: sec.id, beforeId: st.id })} className="-ml-4" />
                            )
                          ) : null
                        }
                      >
                        {(dragListeners) => (
                          <>
                        {editing?.kind === "step" && editing.id === st.id ? (
                          <RenameInput initial={st.name} onCommit={(v) => commitRename("step", st.id, v, st.name)} onCancel={() => setEditing(null)} />
                        ) : (
                          <button
                            type="button"
                            aria-current={active ? "step" : undefined}
                            onClick={() => onSelectStep(st.id)}
                            {...dragListeners}
                            className={cn(
                              // A row is a link to its step (pointer); the grab hand only over the handle (owner 2026-11-03).
                              "group/step flex min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-md border-0 px-2 py-1.5 text-left text-sm transition-colors",
                              active ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-muted bg-transparent",
                            )}
                          >
                            <Icon aria-hidden="true" className={cn("size-3.5 shrink-0", active ? "" : "opacity-70")} />
                            <span className="truncate">{st.name}</span>
                            {draggable ? <GripVertical aria-hidden="true" className="ml-auto size-3.5 shrink-0 cursor-grab opacity-0 group-hover/step:opacity-60 hover:opacity-100" /> : null}
                          </button>
                        )}
                        {!disabled ? (
                          <StepActionsMenu
                            step={st}
                            section={sec}
                            sections={sections}
                            gate={gate}
                            m={m}
                            onRename={() => setEditing({ kind: "step", id: st.id })}
                            onInsert={canEditStructure ? (beforeId) => setAdding({ kind: "step", sectionId: sec.id, beforeId }) : undefined}
                          />
                        ) : null}
                          </>
                        )}
                      </StepRow>
                    );
                  })}
                  {!disabled && q === "" ? (
                    adding?.kind === "step" && adding.sectionId === sec.id && adding.beforeId === undefined ? (
                      <li className="pl-2">
                        <RenameInput initial="" placeholder={t("manualEditor.stepName")} onCommit={commitAdd} onCancel={() => setAdding(null)} />
                      </li>
                    ) : (
                      <li>
                        {sec.steps.length > 0 ? (
                          <InsertSlot label={t("manualEditor.insertStepAtEnd")} hidden={dragged !== null} onPress={() => setAdding({ kind: "step", sectionId: sec.id })} className="-ml-4" />
                        ) : null}
                        {/* With a mouse the plus lines replace this button. It stays on touch, where they are gone, and in an
                            empty section, which has no line to hover. */}
                        <Button variant="outline" size="sm" onPress={() => setAdding({ kind: "step", sectionId: sec.id })} className={cn("dark:border-border gap-1.5", sec.steps.length > 0 && "pointer-fine:hidden")}>
                          <Plus aria-hidden="true" className="size-3.5" />
                          {t("manualEditor.addStep")}
                        </Button>
                      </li>
                    )
                  ) : null}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ul>
      <DragOverlay dropAnimation={null}>
        {dragged ? <div className="bg-popover text-popover-foreground border-border w-fit max-w-60 truncate rounded-md border px-2 py-1.5 text-sm shadow-md">{dragged.name}</div> : null}
      </DragOverlay>
      </DndContext>

      {!disabled ? (
        <div className="border-border border-t p-2">
          {adding?.kind === "section" && adding.beforeId === undefined ? (
            <RenameInput initial="" placeholder={t("manualEditor.sectionName")} onCommit={commitAdd} onCancel={() => setAdding(null)} />
          ) : (
            <Button variant="outline" size="sm" onPress={() => setAdding({ kind: "section" })} className="dark:border-border w-full gap-1.5">
              <FolderPlus aria-hidden="true" className="size-3.5" />
              {t("manualEditor.addSection")}
            </Button>
          )}
        </div>
      ) : null}
    </nav>
  );
}

/** A section header, which also takes a dragged step: dropped here, it goes to the end of that section. */
function SectionDropZone({ sectionId, disabled, isTarget, children }: { sectionId: number; disabled: boolean; isTarget: boolean; children: ReactNode }) {
  const { setNodeRef } = useDroppable({ id: `section:${sectionId}`, data: { sectionId }, disabled });
  return (
    <div ref={setNodeRef} className={cn("group/section flex items-center gap-1 rounded-md", isTarget && "ring-primary ring-2")}>
      {children}
    </div>
  );
}

/** One step row: its insert slot above it, draggable by the name button, and a drop target with a line on the near edge. */
function StepRow({
  step,
  draggable,
  dropEdge,
  slot,
  children,
}: {
  step: { id: number; name: string };
  draggable: boolean;
  dropEdge: "top" | "bottom" | null;
  slot: ReactNode;
  children: (dragListeners: ReturnType<typeof useDraggable>["listeners"]) => ReactNode;
}) {
  const data = { stepId: step.id, name: step.name };
  const drag = useDraggable({ id: `step:${step.id}`, data, disabled: !draggable });
  const drop = useDroppable({ id: `step:${step.id}`, data, disabled: !draggable });
  return (
    <li>
      {slot}
      <div ref={(el) => { drag.setNodeRef(el); drop.setNodeRef(el); }} className={cn("relative flex items-center gap-1", drag.isDragging && "opacity-40")}>
        {dropEdge ? <span aria-hidden="true" className={cn("bg-primary absolute inset-x-0 h-0.5 rounded-full", dropEdge === "top" ? "-top-1" : "-bottom-1")} /> : null}
        {children(drag.listeners)}
      </div>
    </li>
  );
}
